import apiClient from "./client";

/**
 * Clock Records API Service
 */
export const clockRecordApi = {
  /**
   * Get all clock records with optional filters
   * @param {Object} params - Query parameters
   * @param {string} params.fromDate - Start date (ISO format)
   * @param {string} params.toDate - End date (ISO format)
   * @param {string} params.employeeId - Filter by employee ID
   */
  getAll: (params = {}) => {
    return apiClient.get("/clockrecords", params);
  },

  /**
   * Get clock record by ID
   * @param {string} id - Clock record ID (GUID)
   */
  getById: (id) => {
    return apiClient.get(`/clockrecords/${id}`);
  },

  /**
   * Create a new clock record (manual entry)
   * @param {Object} record - Clock record data
   */
  create: (record) => {
    return apiClient.post("/clockrecords", record);
  },

  /**
   * Update a clock record
   * @param {string} id - Clock record ID
   * @param {Object} record - Updated clock record data
   */
  update: (id, record) => {
    return apiClient.put(`/clockrecords/${id}`, record);
  },

  /**
   * Get total hours worked for an employee
   * @param {string} employeeId - Employee ID
   * @param {string} fromDate - Start date (ISO format)
   * @param {string} toDate - End date (ISO format)
   */
  getTotalHours: (employeeId, fromDate, toDate) => {
    return apiClient.get(`/clockrecords/employee/${employeeId}/hours`, {
      fromDate,
      toDate,
    });
  },

  /**
   * Get hours summary by employee
   * @param {string} fromDate - Start date (ISO format)
   * @param {string} toDate - End date (ISO format)
   */
  getSummaryByEmployee: (fromDate, toDate) => {
    return apiClient.get("/clockrecords/summary/by-employee", {
      fromDate,
      toDate,
    });
  },

  /**
   * Sync clock records from an HR provider
   * @param {string} providerName - HR provider name (e.g., 'Sona')
   * @param {string} fromDate - Start date (ISO format)
   * @param {string} toDate - End date (ISO format)
   */
  syncFromProvider: (providerName, fromDate, toDate) => {
    return apiClient.post(`/clockrecords/sync/${providerName}`, null, {
      params: { fromDate, toDate },
    });
  },

  /**
   * Check clock record availability for the current tenant
   * @param {Object} params - Query parameters
   * @param {string} params.fromDate - Start date (ISO format, optional)
   * @param {string} params.toDate - End date (ISO format, optional)
   * @returns {Promise} API response with { hasRecords, recordCount, hasIntegrationRecords }
   */
  checkAvailability: (params = {}) => {
    return apiClient.get("/clockrecords/availability", params);
  },

  /**
   * Import clock records from a CSV file
   * @param {File} file - CSV file to import
   */
  importCsv: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return apiClient.postFormData("/clockrecords/import-csv", formData);
  },
};

export default clockRecordApi;
